import { useMemo } from 'react';
import sovScoresRaw from '../assets/sov_scores.json';
import comparisonMatrixRaw from '../assets/digital_workplace_comparison_matrix_v2.json';
import {
  WorkplaceLevel,
  SealDefinition,
  SovScoresData,
  AggregatedProvider,
  WorkplaceMatrixData,
  SubPoint,
} from '../types/workplace';
import { OTHER_PROVIDER_PREFIX, OTHER_CATEGORIES } from '../utils/otherProviderUtils';

interface UseWorkplaceSovDataResult {
  sovData: SovScoresData;
  levels: WorkplaceLevel[];
  subPointIds: string[];
  aggregatedProviders: AggregatedProvider[];
  sealDefinitions: SealDefinition[];
  error: string | null;
}

export function useWorkplaceSovData(): UseWorkplaceSovDataResult {
  return useMemo(() => {
    try {
      const sovData = sovScoresRaw as unknown as SovScoresData;
      const matrixData = comparisonMatrixRaw as unknown as WorkplaceMatrixData;
      const levels = matrixData.levels;
      const sealDefinitions = matrixData.sealDefinitions;

      // Collect all sub-points and their IDs
      const allSubPoints: SubPoint[] = levels.flatMap(l => l.subPoints);
      const subPointIds = allSubPoints.map(sp => sp.id);

      // --- Validation ---
      const validDeploymentIds = new Set(
        sovData.metadata.deploymentModels.map((d) => d.id)
      );
      const sovObjectiveIds = sovData.metadata.sovObjectives.map((o) => o.id);

      // Validate deployment models and score completeness for all SOV entries
      for (const [name, pd] of Object.entries(sovData.providerDeployments)) {
        for (const dep of pd.availableDeployments) {
          if (!validDeploymentIds.has(dep)) {
            console.warn(`Unknown deployment "${dep}" for provider "${name}"`);
          }
        }
        for (const [dep, scores] of Object.entries(pd.scores)) {
          if (!validDeploymentIds.has(dep as any)) {
            console.warn(`Unknown deployment "${dep}" in scores for "${name}"`);
          }
          const missing = sovObjectiveIds.filter((id) => !(id in scores));
          if (missing.length > 0) {
            console.warn(
              `Provider "${name}" deployment "${dep}" missing SOV scores: ${missing.join(', ')}`
            );
          }
        }
      }

      // --- Build AggregatedProvider[] from eligibleProviders across all sub-points ---
      // Collect unique provider names and track which sub-points each is eligible for
      const providerEligibility = new Map<string, Set<string>>();

      for (const sp of allSubPoints) {
        if (sp.type === 'provider-selection' && sp.eligibleProviders) {
          for (const providerName of sp.eligibleProviders) {
            if (!providerEligibility.has(providerName)) {
              providerEligibility.set(providerName, new Set());
            }
            providerEligibility.get(providerName)!.add(sp.id);
          }
        }
      }

      // Validate: every provider in eligibleProviders must exist in sov_scores.json
      providerEligibility.forEach((_subPoints, providerName) => {
        if (!sovData.providerDeployments[providerName]) {
          console.warn(
            `Provider "${providerName}" in eligibleProviders has no SOV scores in sov_scores.json`
          );
        }
      });

      // Build aggregated providers, exclude those without SOV scores
      const aggregatedProviders: AggregatedProvider[] = [];
      providerEligibility.forEach((eligibleSubPoints, name) => {
        const sovEntry = sovData.providerDeployments[name];
        if (!sovEntry) {
          return; // Already warned above
        }
        aggregatedProviders.push({
          name,
          planIds: [],
          availableDeployments: sovEntry.availableDeployments,
          hasSovScores: true,
          eligibleFeatureGroups: eligibleSubPoints,
        });
      });

      // --- Add virtual "Other" AggregatedProviders for every provider-selection sub-point ---
      const allProviderSelectionSubPointIds = new Set(
        allSubPoints.filter(sp => sp.type === 'provider-selection').map(sp => sp.id)
      );
      const OTHER_PROVIDERS = OTHER_CATEGORIES.map(cat => `${OTHER_PROVIDER_PREFIX}${cat}`);
      for (const name of OTHER_PROVIDERS) {
        const sovEntry = sovData.providerDeployments[name];
        if (sovEntry) {
          aggregatedProviders.push({
            name,
            planIds: [],
            availableDeployments: sovEntry.availableDeployments,
            hasSovScores: true,
            eligibleFeatureGroups: allProviderSelectionSubPointIds,
          });
        }
      }

      return {
        sovData,
        levels,
        subPointIds,
        aggregatedProviders,
        sealDefinitions,
        error: null,
      };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load workplace SOV data';
      return {
        sovData: { metadata: { description: '', maxScore: 4, sovObjectives: [], deploymentModels: [] }, providerDeployments: {} },
        levels: [],
        subPointIds: [],
        aggregatedProviders: [],
        sealDefinitions: [],
        error: msg,
      };
    }
  }, []);
}
